import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, Typography, Autocomplete, TextField, CircularProgress, useTheme } from "@mui/material";
import { fetchAllStrategies } from "../../../../utils/restAPI";

/**
 * ComparePanel
 *
 * Self-contained entry point for strategy comparison.
 * Loads its own strategy options and navigates to the Comparison page on selection.
 *
 * @param {string} ownerSlug - The owner slug of the currently viewed strategy
 * @param {string} mainSlug  - The slug of the currently viewed strategy (Strategy A)
 */
const ComparePanel = ({ ownerSlug, mainSlug }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const loadOptions = async () => {
      setIsLoading(true);
      const response = await fetchAllStrategies();
      if (response.success) setOptions(response.data);
      setIsLoading(false);
    };
    loadOptions();
  }, []);

  const handleChange = (_, selected) => {
    setInputValue("");
    if (!selected) return;
    navigate(`/store/strategy/${ownerSlug}/${mainSlug}/compare/${selected.slug}`);
  };

  return (
    <>
      {isOpen && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            backgroundColor: "rgba(0,0,0,0.15)",
            zIndex: (t) => t.zIndex.modal - 1,
            pointerEvents: "none",
          }}
        />
      )}
      <Card sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: `1px solid ${theme.palette.divider}`,
        mt: 1,
        width: "330px",
        position: "relative",
        zIndex: (t) => isOpen ? t.zIndex.modal : "auto",
      }}>
      {/* <Typography variant="h5">
        Compare strategy
      </Typography> */}
      <Autocomplete
        options={options.filter((option) => option.slug !== mainSlug)}
        getOptionLabel={(option) => option.name || ""}
        getOptionKey={(option) => option.strategyId}
        value={null}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          if (reason !== "reset") setInputValue(newInputValue);
        }}
        onChange={handleChange}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        loading={isLoading}
        renderOption={(props, option) => (
          <li {...props}>
            <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
              <Typography variant="body2">{option.name}</Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Compare strategy"
            placeholder="Type to search..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Card>
    </>
  );
};

export default ComparePanel;
